
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logSecurityEvent } from '@/utils/securityAudit';

interface RateLimitOptions {
  identifier: string;
  action: string;
  maxAttempts: number;
  windowMinutes: number;
}

interface SecurityViolation {
  identifier: string;
  violation_type: string;
  violation_count: number;
  penalty_until: string | null;
}

export const useSecureRateLimit = () => {
  const [isBlocked, setIsBlocked] = useState(false);

  const checkRateLimit = useCallback(async (options: RateLimitOptions): Promise<boolean> => {
    try {
      const { identifier, action, maxAttempts, windowMinutes } = options;
      
      // First check for existing security violations (exponential backoff)
      const { data: violations, error: violationError } = await supabase
        .from('security_violations')
        .select('*')
        .eq('identifier', identifier)
        .eq('violation_type', action)
        .single();

      if (violationError && violationError.code !== 'PGRST116') {
        console.error('Failed to check security violations:', violationError);
        // FAIL CLOSED - block on error for security
        setIsBlocked(true);
        return true;
      }

      // Check if currently under penalty
      if (violations?.penalty_until) {
        const penaltyEnd = new Date(violations.penalty_until);
        if (penaltyEnd > new Date()) {
          setIsBlocked(true);
          await logSecurityEvent({
            event_type: 'RATE_LIMIT_PENALTY_ACTIVE',
            details: { 
              identifier, 
              action, 
              penalty_until: violations.penalty_until,
              violation_count: violations.violation_count
            }
          });
          return true;
        }
      }

      const windowStart = new Date(Date.now() - windowMinutes * 60 * 1000);

      // Check existing attempts in current window
      const { data: existing, error: fetchError } = await supabase
        .from('rate_limits')
        .select('attempts, window_start')
        .eq('identifier', identifier)
        .eq('action', action)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Rate limit check failed:', fetchError);
        // FAIL CLOSED - block on error for security
        setIsBlocked(true);
        return true;
      }

      let currentAttempts = 1;
      let shouldBlock = false;

      if (existing) {
        const existingWindowStart = new Date(existing.window_start);
        
        if (existingWindowStart > windowStart) {
          // Within rate limit window
          currentAttempts = existing.attempts + 1;
          shouldBlock = currentAttempts > maxAttempts;

          await supabase
            .from('rate_limits')
            .update({ attempts: currentAttempts })
            .eq('identifier', identifier)
            .eq('action', action);
        } else {
          // Reset window
          await supabase
            .from('rate_limits')
            .update({ 
              attempts: 1, 
              window_start: new Date().toISOString() 
            })
            .eq('identifier', identifier)
            .eq('action', action);
        }
      } else {
        // Create new rate limit entry
        await supabase
          .from('rate_limits')
          .insert([{
            identifier,
            action,
            attempts: 1,
            window_start: new Date().toISOString()
          }]);
      }

      if (shouldBlock) {
        // Apply exponential backoff penalty
        const violationCount = (violations?.violation_count || 0) + 1;
        const penaltyMinutes = Math.min(Math.pow(2, violationCount), 1440); // Max 24 hours
        const penaltyUntil = new Date(Date.now() + penaltyMinutes * 60 * 1000);

        // Record or update security violation
        await supabase
          .from('security_violations')
          .upsert({
            identifier,
            violation_type: action,
            violation_count: violationCount,
            last_violation: new Date().toISOString(),
            penalty_until: penaltyUntil.toISOString()
          }, {
            onConflict: 'identifier,violation_type'
          });

        await logSecurityEvent({
          event_type: 'RATE_LIMIT_EXCEEDED_WITH_PENALTY',
          details: { 
            identifier, 
            action, 
            attempts: currentAttempts,
            violation_count: violationCount,
            penalty_minutes: penaltyMinutes
          }
        });
        
        setIsBlocked(true);
      }

      return shouldBlock;
    } catch (error) {
      console.error('Rate limit check failed:', error);
      // FAIL CLOSED - block on error for security
      setIsBlocked(true);
      return true;
    }
  }, []);

  const clearViolations = useCallback(async (identifier: string) => {
    try {
      await supabase
        .from('security_violations')
        .delete()
        .eq('identifier', identifier);
      
      setIsBlocked(false);
    } catch (error) {
      console.error('Failed to clear violations:', error);
    }
  }, []);

  return { checkRateLimit, isBlocked, clearViolations };
};
